import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/AuthContext.jsx';
import { Avatar, Stars, Loading, Empty, SectionHeader } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { useNowMinute } from '../../lib/hooks.js';
import { currency, csvList, formatDayLabel, timeRange, relativeTime, isSlotExpired } from '../../lib/format.js';
import ServiceIcon from '../../components/ServiceIcon.jsx';
import NoAvailabilityDialog from '../../components/NoAvailabilityDialog.jsx';

/** Small labelled block used in the credentials panel. */
function Credential({ icon, label, children }) {
  return (
    <div className="row-tight" style={{ alignItems: 'flex-start', gap: 'var(--s-3)' }}>
      <span className="icon-tile icon-tile-neutral" style={{ width: 34, height: 34 }}>
        <Icon name={icon} size={16} />
      </span>
      <div className="grow">
        <span className="label">{label}</span>
        <div className="small" style={{ marginTop: 2 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function NurseProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [nurse, setNurse] = useState(null);
  const [error, setError] = useState('');
  const [blocked, setBlocked] = useState(false);
  const now = useNowMinute(); // drops today's lapsed windows as the clock moves

  useEffect(() => {
    api.get(`/api/nurses/${id}`).then(setNurse).catch((e) => setError(e.message));
  }, [id]);

  if (error)
    return (
      <Empty
        art="not-found"
        title="Profile unavailable"
        action={
          <Link to="/search" className="btn btn-primary">
            Back to search
          </Link>
        }
      >
        {error}
      </Empty>
    );
  if (!nurse) return <Loading rows={3} />;

  const languages = csvList(nurse.languages);
  const areas = csvList(nurse.service_areas);
  const certifications = csvList(nurse.certifications);

  // Group the next 14 days of open windows, hiding those already booked and —
  // for today — those that have run out. The test is on the window's END: one
  // that started earlier today still has bookable start times left, and the
  // wizard these chips link to will offer exactly those.
  const byDate = {};
  for (const slot of nurse.availability) {
    const taken = nurse.bookedSlots.some(
      (b) => b.date === slot.date && b.start_time < slot.end_time && slot.start_time < b.end_time
    );
    if (taken || isSlotExpired(slot.date, slot.end_time, now)) continue;
    (byDate[slot.date] ||= []).push(slot);
  }
  const openDates = Object.keys(byDate).sort().slice(0, 7);
  // Everything this nurse had left was today, and it has now lapsed — say so
  // rather than implying they have no calendar at all.
  const onlyTodayExpired =
    openDates.length === 0 && nurse.availability.some((s) => isSlotExpired(s.date, s.end_time, now));

  const bookHref = user?.role === 'admin' ? `/admin/book/${nurse.id}` : `/book/${nurse.id}`;
  // Server-computed: published, future, unbooked windows and still on duty.
  const bookable = nurse.is_bookable !== false;

  return (
    <>
      <Link to="/search" className="back-link">
        <Icon name="arrow-left" />
        Back to search
      </Link>

      {/* ----------------------------------------------------- Identity */}
      <div className="card card-pad mb-3">
        <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap', gap: 'var(--s-5)' }}>
          <Avatar name={nurse.name} avatar={nurse.avatar} size="lg" ring />
          <div className="grow">
            <div className="row-tight" style={{ flexWrap: 'wrap' }}>
              <h1>{nurse.name}</h1>
              {nurse.verification_status === 'verified' && (
                <span className="pill pill-success">
                  <Icon name="shield-check" /> Verified
                </span>
              )}
            </div>
            <p className="muted" style={{ margin: '6px 0 12px' }}>
              {nurse.headline}
            </p>
            <div className="nurse-meta">
              <Stars value={nurse.rating_avg} count={nurse.rating_count} />
              <span>
                <Icon name="briefcase" />
                {nurse.experience_years} years experience
              </span>
              <span>
                <Icon name="check-circle" />
                {nurse.jobs_completed} visits completed
              </span>
              <span>
                <Icon name="map-pin" />
                {[nurse.city, nurse.state].filter(Boolean).join(', ')}
                {nurse.distance_km != null && ` · ${nurse.distance_km} km away`}
              </span>
            </div>
          </div>
        </div>

        <hr className="divider" />

        <div className="row" style={{ alignItems: 'center' }}>
          <div>
            <div className="rate">{currency(nurse.hourly_rate)}</div>
            <small className="rate-unit">per hour · billed by the hour</small>
          </div>
          <div className="spacer" />
          {bookable ? (
            // An admin books on behalf of a patient; a patient (or signed-out
            // visitor) books for themselves.
            <Link to={bookHref} className="btn btn-primary btn-lg">
              <Icon name="calendar" />
              {user?.role === 'admin' ? 'Book for a patient' : 'Book this nurse'}
            </Link>
          ) : (
            <div className="row-tight" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <span className="pill pill-warning">
                <Icon name={nurse.is_available ? 'calendar' : 'bell-off'} />
                {nurse.is_available ? 'No open time slots' : 'Not accepting bookings'}
              </span>
              <button type="button" className="btn btn-primary btn-lg" onClick={() => setBlocked(true)}>
                <Icon name="calendar" />
                {user?.role === 'admin' ? 'Book for a patient' : 'Book this nurse'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="detail-layout">
        <div className="stack">
          {nurse.bio && (
            <section className="card">
              <div className="card-header">
                <h2>About {nurse.name.split(' ')[0]}</h2>
              </div>
              <div className="card-body">
                <p className="muted">{nurse.bio}</p>
              </div>
            </section>
          )}

          <section className="card">
            <div className="card-header">
              <h2 className="grow">Services offered</h2>
              <span className="tiny dim">Tap one to start a booking</span>
            </div>
            <div className="card-body">
              <div className="grid grid-4">
                {nurse.services.map((s, i) => (
                  <Link
                    key={s.id}
                    to={`/book/${nurse.id}?serviceId=${s.id}`}
                    className="service-tile anim-rise"
                    style={{ '--i': i }}
                  >
                    <span className="ico" aria-hidden="true">
                      <ServiceIcon service={s} size={26} />
                    </span>
                    <span className="nm">{s.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h2 className="grow">Reviews</h2>
              <Stars value={nurse.rating_avg} count={nurse.rating_count} />
            </div>
            <div className="card-body">
              {nurse.reviews.length === 0 ? (
                <Empty art="support" title="No reviews yet">
                  Be the first to book {nurse.name.split(' ')[0]} and share how it went.
                </Empty>
              ) : (
                nurse.reviews.map((r) => (
                  <div key={r.id} className="review">
                    <div className="review-head">
                      <Avatar name={r.patient_name} size="sm" />
                      <div className="grow">
                        <div className="bold small">{r.patient_name}</div>
                        <div className="tiny dim">
                          {r.service_name} · {relativeTime(r.created_at)}
                        </div>
                      </div>
                      <Stars value={r.rating} />
                    </div>
                    {r.comment && <p>{r.comment}</p>}
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        <aside className="stack sticky-aside">
          <section className="card">
            <div className="card-header">
              <h3 className="grow">Next available</h3>
              <Icon name="calendar" size={16} className="dim" />
            </div>
            <div className="card-body stack-sm">
              {openDates.length === 0 ? (
                <p className="dim small">
                  {onlyTodayExpired
                    ? 'No available time slots remaining for today.'
                    : 'No open slots in the next few weeks.'}
                </p>
              ) : (
                openDates.map((d) => (
                  <div key={d}>
                    <div className="bold small mb-1">{formatDayLabel(d)}</div>
                    <div className="chips">
                      {byDate[d].map((s) => (
                        <Link
                          key={s.id}
                          to={`/book/${nurse.id}?date=${d}&start=${s.start_time}&end=${s.end_time}`}
                          className="chip"
                        >
                          {timeRange(s.start_time, s.end_time)}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h3>Qualifications</h3>
            </div>
            <div className="card-body stack">
              <Credential icon="award" label="Qualifications">
                {nurse.qualifications || '—'}
              </Credential>

              {certifications.length > 0 && (
                <Credential icon="file-check" label="Certifications">
                  <div className="chips mt-1">
                    {certifications.map((c) => (
                      <span key={c} className="chip chip-static">
                        {c}
                      </span>
                    ))}
                  </div>
                </Credential>
              )}

              <Credential icon="languages" label="Languages">
                {languages.join(', ') || '—'}
              </Credential>

              <Credential icon="map" label="Service areas">
                {areas.join(', ') || nurse.city}
              </Credential>
            </div>
          </section>

          <div className="banner banner-neutral" style={{ marginBottom: 0 }}>
            <Icon name="lock" />
            <span className="grow tiny">
              Your address and phone number are shared only after {nurse.name.split(' ')[0]} accepts the
              booking, and only for that visit.
            </span>
          </div>
        </aside>
      </div>

      {blocked && <NoAvailabilityDialog nurse={nurse} onClose={() => setBlocked(false)} />}
    </>
  );
}
